import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface PaginationProps {
  page: number;
  pageCount: number;
  total: number;
  from: number;
  to: number;
  onPrev: () => void;
  onNext: () => void;
  className?: string;
}

/**
 * Presentational footer control for client-side paginated tables. Renders a
 * "Showing X–Y of N" summary on the left and Prev / "Page X / Y" / Next on the
 * right. Designed to sit directly under a table inside the same Card.
 */
export default function Pagination({
  page,
  pageCount,
  total,
  from,
  to,
  onPrev,
  onNext,
  className,
}: PaginationProps) {
  const canPrev = page > 1;
  const canNext = page < pageCount;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 text-sm",
        className,
      )}
    >
      <p className="text-muted-foreground">
        {total === 0 ? (
          "No records"
        ) : (
          <>
            Showing <span className="font-medium text-foreground">{from}</span>–
            <span className="font-medium text-foreground">{to}</span> of{" "}
            <span className="font-medium text-foreground">{total}</span>
          </>
        )}
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPrev}
          disabled={!canPrev}
        >
          <ChevronLeft className="h-4 w-4" />
          Prev
        </Button>
        <span className="text-muted-foreground tabular-nums">
          Page {page} / {pageCount}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={onNext}
          disabled={!canNext}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
